import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Send, Briefcase, X, MessageCircle, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { aiManagerChat, getAiHistory, getManagerCapacity, managerTask, waStatus, waBuy, waLinkStart, waUnlink } from "@/lib/afmApi";

// Mánager SONA en la web: el MISMO de la app (memoria, gestiones y clave de IA de SONA).
const errMsg = (e, d) => e?.response?.data?.detail || d;
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString("es-VE", { day: "numeric", month: "short" }); } catch { return ""; } };

function WhatsAppCard({ userId }) {
  const [st, setSt] = useState(null);
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => waStatus(userId).then(setSt).catch(() => setSt(null));
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!st) return null;
  const run = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e, "No se pudo completar")); } finally { setBusy(false); } };
  const until = st.addon_until ? fmtDay(st.addon_until) : "";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="wa-card">
      <div className="mb-2 flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" /><h3 className="font-serif text-lg font-bold text-black">Tu Mánager por WhatsApp</h3></div>
      {!st.entitled ? (
        st.addon_available ? (
          <>
            <p className="text-sm text-slate-600">Habla con tu Mánager y prepara lanzamientos desde WhatsApp. Mismo Mánager, misma memoria y mismas gestiones.</p>
            <button disabled={busy} onClick={() => run(async () => setSt(await waBuy(userId)), "Activado por 30 días")} className="mt-3 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-bold text-black disabled:opacity-50">Activar por ${Number(st.addon_price_usd).toFixed(2)} / 30 días</button>
            <p className="mt-2 text-xs text-slate-500">Se paga con el saldo de tu billetera SONA. Incluido en el plan Anual.</p>
          </>
        ) : <p className="text-sm text-slate-600">Disponible desde el plan Digital (como complemento) e incluido en el plan Anual.</p>
      ) : !st.ready ? (
        <p className="text-sm text-slate-600">✓ Activo{st.via === "addon" && until ? ` hasta el ${until}` : ""}. Estamos terminando de configurar el número de SONA; pronto podrás conectarlo.</p>
      ) : st.linked ? (
        <>
          <p className="text-sm text-slate-600">✓ Conectado al {st.phone}{st.via === "addon" && until ? ` · activo hasta el ${until}` : ""}. Escríbele <b>menu</b> para ver lo que puede hacer.</p>
          <div className="mt-3 flex gap-2">
            <a href={`https://wa.me/${st.number}`} target="_blank" rel="noreferrer" className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-bold text-black">Abrir WhatsApp</a>
            <button disabled={busy} onClick={() => run(async () => { await waUnlink(userId); setCode(null); await load(); }, "WhatsApp desconectado")} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:border-black">Desconectar</button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">✓ Activo{st.via === "addon" && until ? ` hasta el ${until}` : ""}. Para conectar tu número, envía el código desde tu WhatsApp.</p>
          {code && <div className="my-3 text-center"><div className="font-mono text-3xl font-bold tracking-widest text-black">SONA {code.code}</div><div className="text-xs text-slate-500">Envíalo a +{code.number} · vence en {code.expires_in_min} min</div></div>}
          <div className="mt-3 flex flex-wrap gap-2">
            <button disabled={busy} onClick={() => run(async () => { const r = await waLinkStart(userId); setCode(r); window.open(r.wa_link, "_blank", "noopener"); })} className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-bold text-black disabled:opacity-50">{code ? "Nuevo código" : "Conectar WhatsApp"}</button>
            {code && <button onClick={load} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:border-black">Ya lo envié</button>}
          </div>
        </>
      )}
    </div>
  );
}

export default function ManagerSona() {
  const { user } = useAuth();
  const [cap, setCap] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [task, setTask] = useState(null);
  const [brief, setBrief] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    getManagerCapacity(user.id).then(setCap);
    getAiHistory(user.id).then((h) => setMessages(h.length ? h : [{ role: "assistant", content: "¡Hola! Soy tu Mánager SONA. Cuéntame en qué estás trabajando y te ayudo con tu próximo lanzamiento." }]));
  }, [user.id]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, sending]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput(""); setMessages((m) => [...m, { role: "user", content: msg }]); setSending(true);
    try {
      const r = await aiManagerChat({ user_id: user.id, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: r.reply || "…" }]);
    } catch (e) { setMessages((m) => [...m, { role: "assistant", content: errMsg(e, "Ahora mismo no puedo responder. Intenta de nuevo en un momento.") }]); }
    finally { setSending(false); }
  };

  const runTask = async () => {
    if (!task) return;
    setSending(true);
    const t = task; setTask(null);
    setMessages((m) => [...m, { role: "user", content: `📋 Gestión: ${t.label}${brief.trim() ? `\n${brief.trim()}` : ""}` }]);
    try {
      const r = await managerTask({ user_id: user.id, task: t.key, brief: brief.trim() });
      setMessages((m) => [...m, { role: "assistant", content: r.reply?.text || "…" }]);
      if (r.capacity) setCap(r.capacity);
    } catch (e) { toast.error(errMsg(e, "No se pudo completar la gestión")); }
    finally { setSending(false); setBrief(""); }
  };

  const pct = cap && cap.capacity ? Math.min(100, Math.round((cap.used / cap.capacity) * 100)) : 0;
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]" data-testid="manager-page">
      <div className="flex h-[75vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-3 border-b border-slate-100 bg-black px-5 py-3 text-white">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#AC3333]"><Bot className="h-5 w-5" /></div>
          <div className="flex-1"><div className="flex items-center gap-1.5 text-sm font-bold"><Sparkles className="h-3.5 w-3.5" /> Mánager SONA</div><div className="text-[11px] text-white/60">El mismo de la app · tu memoria y tus gestiones</div></div>
        </div>
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-5">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-black text-white" : "border border-slate-200 bg-slate-50 text-slate-800"}`}>{m.content}</div>
            </div>
          ))}
          {sending && <div className="text-sm text-slate-400">Pensando…</div>}
        </div>
        <div className="flex items-end gap-2 border-t border-slate-100 p-3">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Escríbele a tu Mánager…" className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-black" />
          <button onClick={send} disabled={sending || !input.trim()} className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#AC3333] text-white disabled:opacity-40"><Send className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="space-y-5">
        {cap && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="manager-capacity">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Plan {cap.plan_label}</div>
            {cap.plan === "staff" ? <p className="mt-1 text-sm text-slate-600">Cuenta del equipo: sin límite de gestiones.</p> : cap.plan === "free" ? (
              <p className="mt-1 text-sm text-slate-600">Diagnóstico inicial {cap.free_diagnostic_available ? "disponible" : "ya usado"}. El chat siempre está abierto.</p>
            ) : (
              <>
                <div className="mt-1 font-serif text-2xl font-bold text-black">{cap.available} de {cap.capacity}</div>
                <div className="text-xs text-slate-500">gestiones disponibles · se renuevan el {fmtDay(cap.resets_on)}</div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-[#AC3333]" style={{ width: `${100 - pct}%` }} /></div>
              </>
            )}
            <div className="mt-4 space-y-2">
              {(cap.tasks || []).map((t) => (
                <button key={t.key} disabled={!t.allowed || sending} onClick={() => { setTask(t); setBrief(""); }} title={t.reason || t.desc}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-left text-sm hover:border-black disabled:cursor-not-allowed disabled:opacity-50">
                  <span className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5 text-[#AC3333]" />{t.label}</span>
                  <span className="text-xs text-slate-500">{t.allowed ? (t.cost ? `${t.cost} gest.` : "gratis") : t.reason}</span>
                </button>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-slate-400">Conversar no gasta gestiones. Las gestiones son trabajos completos (planes, calendarios, textos).</p>
          </div>
        )}
        <WhatsAppCard userId={user.id} />
      </div>

      {task && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setTask(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between"><h3 className="font-serif text-lg font-bold">{task.label}</h3><button onClick={() => setTask(null)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <p className="text-sm text-slate-600">{task.desc}</p>
            <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={4} maxLength={1500} placeholder={task.brief_hint} className="mt-3 w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-black" />
            <button onClick={runTask} className="mt-3 w-full rounded-lg bg-black py-2.5 text-sm font-semibold text-white">Usar {task.cost ? `${task.cost} gestiones` : "mi diagnóstico gratis"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
